// The road-case state vocabulary, in ONE place (ART-253, legacy parity). The server owns the values
// (`RoadCaseStates`: home | in_venue | on_truck, a CHECK constraint); this module owns the words, the manual cycle,
// the sort order and the roll-up line, so the home card, the case sheet, /scan and scan mode cannot drift.

export type CaseState = "home" | "in_venue" | "on_truck";

export const CASE_STATES: readonly CaseState[] = ["home", "in_venue", "on_truck"];

export const CASE_STATE_LABEL: Record<CaseState, string> = {
  home: "Home",
  in_venue: "In venue",
  on_truck: "On truck",
};

/** A server value → a known state. An unknown value reads as `home` rather than rendering a raw word. */
export function asCaseState(raw: string | null | undefined): CaseState {
  return raw === "in_venue" || raw === "on_truck" ? raw : "home";
}

/** The row pill's manual cycle: Home → In venue → On truck → Home. A MANUAL tap only — a sticker scan never
 * cycles, it sets the scan-mode target (the legacy rewrite ruling). */
export function nextCaseState(state: CaseState): CaseState {
  return CASE_STATES[(CASE_STATES.indexOf(state) + 1) % CASE_STATES.length]!;
}

/** Rows sort by state (home, then in venue, then on truck), then by name — legacy's order: what is still to be
 * moved floats to the top. */
export function sortCases<T extends { state?: string | null; name?: string | null }>(cases: readonly T[]): T[] {
  return [...cases].sort(
    (a, b) =>
      CASE_STATES.indexOf(asCaseState(a.state)) - CASE_STATES.indexOf(asCaseState(b.state)) ||
      (a.name ?? "").localeCompare(b.name ?? ""),
  );
}

/** The checklist's roll-up sub-line, legacy's exact wording: "All 6 at home" / "2 in venue · 3 on truck of 6". */
export function caseRollup(cases: readonly { state?: string | null }[]): string {
  const total = cases.length;
  if (total === 0) return "No cases yet — add your first";
  const inVenue = cases.filter((c) => asCaseState(c.state) === "in_venue").length;
  const onTruck = cases.filter((c) => asCaseState(c.state) === "on_truck").length;
  if (inVenue === 0 && onTruck === 0) return `All ${total} at home`;
  const bits: string[] = [];
  if (inVenue) bits.push(`${inVenue} in venue`);
  if (onTruck) bits.push(`${onTruck} on truck`);
  return `${bits.join(" · ")} of ${total}`;
}

/** "3 items" / "1 item" / the empty prompt — the row's meta line. */
export function itemCountLine(n: number | null | undefined): string {
  const count = n ?? 0;
  if (count === 0) return "Tap to add items";
  return `${count} item${count === 1 ? "" : "s"}`;
}

/** Scan mode's three targets, in the picker's order, with legacy's copy. */
export const SCAN_TARGETS: readonly { state: CaseState; title: string; sub: string; prompt: string }[] = [
  { state: "on_truck", title: "On truck", sub: "Loading the truck for the next leg", prompt: "Hold near a case sticker to mark it ON TRUCK." },
  { state: "in_venue", title: "In venue", sub: "Unloading at the venue for the show", prompt: "Hold near a case sticker to mark it IN VENUE." },
  { state: "home", title: "Home", sub: "Cases coming back to home base", prompt: "Hold near a case sticker to send it HOME." },
];
